'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import * as React from 'react';

// Radix UI Select는 빈 문자열을 SelectItem의 value로 사용할 수 없으므로
// 빈 값을 표현하는 특별한 상수 사용
const EMPTY_VALUE = '__EMPTY__';

function BaseSelect({ value, onValueChange, ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  // value 변환: 빈 문자열 → EMPTY_VALUE (undefined는 그대로 유지하여 placeholder 표시)
  const internalValue = value === '' ? EMPTY_VALUE : value;

  // onValueChange 변환: EMPTY_VALUE → 빈 문자열
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (onValueChange) {
        onValueChange((newValue === EMPTY_VALUE ? '' : newValue) as string);
      }
    },
    [onValueChange],
  );

  return <SelectPrimitive.Root data-slot="select" value={internalValue} onValueChange={handleValueChange} {...props} />;
}

function BaseSelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function BaseSelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function BaseSelectTrigger({ children, ...props }: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger data-slot="select-trigger" {...props}>
      {children}
    </SelectPrimitive.Trigger>
  );
}

function BaseSelectContent({
  children,
  position = 'popper',
  sideOffset = 8,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        sideOffset={sideOffset}
        style={{ width: 'var(--radix-select-trigger-width)' }}
        {...props}
      >
        <SelectPrimitive.Viewport data-slot="select-viewport" className="w-full">
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function BaseSelectLabel({ ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return <SelectPrimitive.Label data-slot="select-label" {...props} />;
}

function BaseSelectItem({ children, value, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  // value 변환: 빈 문자열 → EMPTY_VALUE
  const internalValue = value === '' ? EMPTY_VALUE : value;

  return (
    <SelectPrimitive.Item data-slot="select-item" value={internalValue} {...props}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function BaseSelectSeparator({ ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return <SelectPrimitive.Separator data-slot="select-separator" {...props} />;
}

function BaseSelectScrollUpButton({ ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton data-slot="select-scroll-up-button" {...props}>
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function BaseSelectScrollDownButton({ ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton data-slot="select-scroll-down-button" {...props}>
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  BaseSelect,
  BaseSelectContent,
  BaseSelectGroup,
  BaseSelectItem,
  BaseSelectLabel,
  BaseSelectScrollDownButton,
  BaseSelectScrollUpButton,
  BaseSelectSeparator,
  BaseSelectTrigger,
  BaseSelectValue,
};
